import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
// material
import {
  Card,
  Stack,
  Container,
  Typography,
  Tab,
  Box,
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TablePagination,
  TextField,
  InputAdornment,
  MenuItem,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
// components
import Page from '../components/Page';
import DashboardBreadcrumbLink from 'src/components/DashboardBreadcrumbLink';
import Iconify from '../components/Iconify';
import Label from '../components/Label';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import ClientCameraForm from 'src/sections/devices/ClientCameraForm';
// api
import { getDevices, deleteDevice } from 'src/api/devices';
import { getTimezoneList } from 'src/api/analytics';
// config
import cameraModelComboClientConfig from '../config/cameraModelComboClientConfig.json';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'sno', label: 'S No', align: 'left' },
  { id: 'deviceid', label: 'Camera Id', align: 'left' },
  { id: 'displayname', label: 'Client Name', align: 'left' },
  { id: 'devicename', label: 'Camera Name', align: 'left' },
  { id: 'TimezoneId', label: 'Time Zone', align: 'left' },
  { id: 'Status', label: 'Status', align: 'left' },
  { id: 'actions', label: '', align: 'right' },
];

// client ids that are allowed to be viewed on this page
const CONFIG_CLIENT_IDS = cameraModelComboClientConfig
  .map((config) => Number(config.client_id))
  .filter((id) => !Number.isNaN(id));

// ----------------------------------------------------------------------

export default function ClientCameras() {
  const theme = useTheme();

  const tabListSx = {
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.neutral,
    '& .MuiTab-root': { color: theme.palette.text.secondary },
    '& .MuiTab-root.Mui-selected': { color: theme.palette.text.primary },
    '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main },
  };

  const userType = localStorage.getItem('user_type');
  const currentClientId = Number(localStorage.getItem('client_id'));
  // user_type 9 is the internal admin, every other login is tied to a single client
  const isClientRestrictedUser = userType !== '9';

  // only the clients listed in cameraModelComboClientConfig.json can be viewed here,
  // and a client login can never see anything outside of its own client id
  const allowedClientIds = useMemo(() => {
    if (!isClientRestrictedUser) return CONFIG_CLIENT_IDS;
    return CONFIG_CLIENT_IDS.filter((id) => id === currentClientId);
  }, [isClientRestrictedUser, currentClientId]);

  const [devices, setDevices] = useState([]);
  const [timezoneDetails, setTimezone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // one dialog drives both add and edit, device === null means add
  const [formDialog, setFormDialog] = useState({ open: false, device: null });

  // a new camera is created against the logged in client, so only a configured client login can add
  const canAddCamera = CONFIG_CLIENT_IDS.includes(currentClientId);

  const handleAdd = () => setFormDialog({ open: true, device: null });
  const handleEdit = (row) => setFormDialog({ open: true, device: row });
  const handleCloseForm = () => setFormDialog((prev) => ({ ...prev, open: false }));

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [showDeleteReasonError, setShowDeleteReasonError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const openDeleteDialog = (row) => {
    setDeleteReason('');
    setShowDeleteReasonError(false);
    setDeleteTarget(row);
  };

  const closeDeleteDialog = () => {
    if (isDeleting) return;
    setDeleteTarget(null);
  };

  const handleDeleteCamera = () => {
    if (!deleteTarget || isDeleting) return;

    if (!deleteReason.trim()) {
      setShowDeleteReasonError(true);
      return;
    }

    setIsDeleting(true);
    deleteDevice(
      {
        deviceid: deleteTarget.deviceid,
        DeleteCommentReason: deleteReason.trim(),
      },
      (response) => {
        setIsDeleting(false);
        if (response.status === 200) {
          const { deviceid } = deleteTarget;
          setDevices((prev) => prev.filter((row) => row.deviceid !== deviceid));
          setDeleteTarget(null);
          setDeleteReason('');
          setShowDeleteReasonError(false);
          loadDevices();
        }
      }
    );
  };

  useEffect(() => {
    getTimezoneList((response) => {
      if (response.status === 200) setTimezone(response.data || []);
    });
  }, []);

  const loadDevices = () => {
    if (allowedClientIds.length === 0) {
      setDevices([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const body = { status: '0,1', limit: 0, client_id: allowedClientIds.join(',') };

    getDevices(body, (response) => {
      if (response.status === 200) {
        const list = Array.isArray(response.data) ? response.data : [];
        // guard the response as well, the page must never show a non configured client
        setDevices(list.filter((row) => allowedClientIds.includes(Number(row.clientid))));
      } else {
        setDevices([]);
      }
      setLoading(false);
    });
  };

  useEffect(() => {
    loadDevices();
  }, [allowedClientIds]);

  // client dropdown is built from the devices themselves, no extra client api call needed
  const clientOptions = useMemo(() => {
    const map = new Map();
    devices.forEach((row) => {
      if (!map.has(row.clientid)) map.set(row.clientid, row.displayname || `Client ${row.clientid}`);
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name).trim().localeCompare(String(b.name).trim()));
  }, [devices]);

  const filteredDevices = useMemo(() => {
    const query = search.trim().toLowerCase();

    return devices.filter((row) => {
      if (clientFilter !== 'all' && Number(row.clientid) !== Number(clientFilter)) return false;
      if (tab === 'active' && row.Status !== 1) return false;
      if (tab === 'inactive' && row.Status !== 0) return false;
      if (!query) return true;

      return [row.devicename, row.deviceid, row.displayname]
        .some((field) => String(field ?? '').toLowerCase().includes(query));
    });
  }, [devices, search, clientFilter, tab]);

  const counts = useMemo(() => {
    const scoped = clientFilter === 'all'
      ? devices
      : devices.filter((row) => Number(row.clientid) === Number(clientFilter));
    return {
      all: scoped.length,
      active: scoped.filter((row) => row.Status === 1).length,
      inactive: scoped.filter((row) => row.Status === 0).length,
    };
  }, [devices, clientFilter]);

  useEffect(() => {
    setPage(0);
  }, [search, clientFilter, tab]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredDevices.length / rowsPerPage) - 1);
    setPage((currentPage) => Math.min(currentPage, lastPage));
  }, [filteredDevices.length, rowsPerPage]);

  const displayedRows = filteredDevices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const isNotFound = !loading && filteredDevices.length === 0 && search.trim().length > 0;
  const isEmpty = !loading && filteredDevices.length === 0 && search.trim().length === 0;

  const getTimezone = (timezoneId) => {
    const timezone = timezoneDetails.find((row) => row.Id === timezoneId);
    return timezone ? timezone.TimeZone : 'Not selected';
  };

  if (allowedClientIds.length === 0) {
    return (
      <Page title="Cameras">
        <Container maxWidth="xxl" sx={{ p: 0 }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ mb: 1 }}>Cameras</Typography>
            <Breadcrumbs separator="•" aria-label="breadcrumb">
              <DashboardBreadcrumbLink sx={{ fontSize: '0.875rem' }} />
              <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>Cameras</Typography>
            </Breadcrumbs>
          </Box>
          <Card sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="subtitle1" gutterBottom>No cameras available</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              This page is only available for the configured clients.
            </Typography>
          </Card>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Cameras">
      <Container maxWidth="xxl" sx={{ p: 0 }}>
        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h4">Cameras</Typography>
            {canAddCamera && (
              <Button
                variant="contained"
                onClick={handleAdd}
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                Add Camera
              </Button>
            )}
          </Box>
          <Breadcrumbs separator="•" aria-label="breadcrumb">
            <DashboardBreadcrumbLink sx={{ fontSize: '0.875rem' }} />
            <Typography color="text.primary" sx={{ fontSize: '0.875rem' }}>Cameras</Typography>
          </Breadcrumbs>
        </Box>

        <ClientCameraForm
          key={formDialog.device?.deviceid || 'new'}
          open={formDialog.open}
          device={formDialog.device}
          clientId={currentClientId}
          onClose={handleCloseForm}
          onSaved={loadDevices}
        />

        <Dialog open={Boolean(deleteTarget)} onClose={closeDeleteDialog} fullWidth maxWidth="sm">
          <DialogTitle>Are you sure you want to delete this camera?</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Typography variant="body2">
                <strong>Camera ID:</strong> {deleteTarget?.deviceid}
              </Typography>
              <Typography variant="body2">
                <strong>Camera Name:</strong> {deleteTarget?.devicename || '-'}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                required
                label="Delete Reason"
                placeholder="Enter reason for deleting this camera"
                InputLabelProps={{ shrink: true }}
                value={deleteReason}
                onChange={(event) => {
                  setDeleteReason(event.target.value);
                  if (showDeleteReasonError && event.target.value.trim()) {
                    setShowDeleteReasonError(false);
                  }
                }}
                error={showDeleteReasonError}
                helperText={showDeleteReasonError ? 'Delete reason is required' : ' '}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} disabled={isDeleting}>
              No
            </Button>
            <Button onClick={handleDeleteCamera} color="error" variant="contained" disabled={isDeleting}>
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        <Card>
          <TabContext value={tab}>
            <TabList onChange={(event, value) => setTab(value)} aria-label="camera status tabs" sx={tabListSx}>
              <Tab
                value="all"
                label="All"
                icon={<Label sx={{ mr: 1 }} color="primary">{counts.all}</Label>}
                iconPosition="start"
                style={{ minHeight: 'auto' }}
              />
              <Tab
                value="active"
                label="Active"
                icon={<Label sx={{ mr: 1 }} color="success">{counts.active}</Label>}
                iconPosition="start"
                style={{ minHeight: 'auto' }}
              />
              <Tab
                value="inactive"
                label="In Active"
                icon={<Label sx={{ mr: 1 }} color="error">{counts.inactive}</Label>}
                iconPosition="start"
                style={{ minHeight: 'auto' }}
              />
            </TabList>

            <TabPanel value={tab} sx={{ p: 0 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ p: 2 }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <TextField
                  size="small"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search camera name, id, client..."
                  sx={{ width: { xs: '100%', sm: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {clientOptions.length > 1 && (
                  <TextField
                    select
                    size="small"
                    label="Client"
                    value={clientFilter}
                    onChange={(event) => setClientFilter(event.target.value)}
                    sx={{ width: { xs: '100%', sm: 260 } }}
                  >
                    <MenuItem value="all">All Clients</MenuItem>
                    {clientOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>

              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {TABLE_HEAD.map((head) => (
                          <TableCell key={head.id} align={head.align}>{head.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {loading && (
                        <TableRow>
                          <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 5 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      )}

                      {!loading && displayedRows.map((row, index) => {
                        const {
                          deviceid, devicename, displayname,
                          TimezoneId, Status,
                        } = row;

                        return (
                          <TableRow hover key={deviceid} tabIndex={-1}>
                            <TableCell align="left">{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell align="left">{deviceid}</TableCell>
                            <TableCell align="left">{displayname}</TableCell>
                            <TableCell align="left">{devicename}</TableCell>
                            <TableCell align="left">{getTimezone(TimezoneId)}</TableCell>
                            <TableCell align="left">
                              <Label variant="ghost" color={Status === 1 ? 'success' : 'error'}>
                                {Status === 1 ? 'Active' : 'In Active'}
                              </Label>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit camera">
                                <IconButton size="small" onClick={() => handleEdit(row)}>
                                  <Iconify icon="eva:edit-fill" width={20} height={20} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete camera">
                                <IconButton size="small" color="error" aria-label="Delete camera" onClick={() => openDeleteDialog(row)}>
                                  <Iconify icon="eva:trash-2-outline" width={20} height={20} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {isEmpty && (
                        <TableRow>
                          <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 5 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              No cameras found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {isNotFound && (
                        <TableRow>
                          <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                            <SearchNotFound searchQuery={search} />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredDevices.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
              />
            </TabPanel>
          </TabContext>
        </Card>
      </Container>
    </Page>
  );
}
